import React, { useState, useEffect } from 'react';

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };

const PRAYER_ACCENT = {
  shacharit: '#1e3a5f',
  mincha:    '#b8860b',
  maariv:    '#374151',
  other:     '#64748b',
};

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const todayDow = new Date().getDay();
const todayHebrewDay = (() => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
})();
const isRoshChodeshToday = todayHebrewDay === 1 || todayHebrewDay === 30;
const isOverrideActive = (days) =>
  (days || []).includes(todayDow) || (isRoshChodeshToday && (days || []).includes('roshChodesh'));

const StatusBox = ({ msg, isError }) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl', fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>
    {msg}
  </div>
);

/**
 * Table — טבלה מסודרת עם שורה לכל תפילה.
 * עמודת שם, עמודת שעה, ועמודת הערות (כלל / חריגות).
 * פס צבעוני בצד ימין מסמן את סוג התפילה.
 * שורות חריגות ימים מוטמעות מתחת לשורה הראשית.
 */
const Table = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', showRuleMode = false, highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <StatusBox msg="טוען..." />;
  if (error) return <StatusBox msg={error} isError />;
  if (!processedCustomTimes?.length) return <StatusBox msg="אין זמני תפילה" />;

  const nowMin = now.getHours() * 60 + now.getMinutes();

  const sorted = [...processedCustomTimes].sort((a, b) =>
    PRAYER_ORDER.indexOf(a.prayer || 'other') - PRAYER_ORDER.indexOf(b.prayer || 'other')
  );

  let nextIdx = -1;
  if (highlightNextMinyan) {
    let bestMin = Infinity;
    sorted.forEach((ct, i) => {
      if (ct.isActiveToday === false) return;
      const activeOverride = (ct.resolvedOverrides || ct.dayOverrides || []).find(ov => isOverrideActive(ov.days));
      const times = ct.type === 'recurring'
        ? (activeOverride ? [toMinutes(activeOverride.resolvedTime)] : (ct.resolvedSlots || []).map(toMinutes).filter(m => m >= 0))
        : (() => { const m = toMinutes(activeOverride ? activeOverride.resolvedTime : ct.resolvedTime); return m >= 0 ? [m] : []; })();
      for (const m of times) {
        if (m >= nowMin && m < bestMin) { bestMin = m; nextIdx = i; }
      }
    });
  }

  return (
    <div style={{
      width: '100%', height: '100%', direction: 'rtl',
      display: 'flex', flexDirection: 'column',
      backgroundColor: '#ffffff', boxSizing: 'border-box', overflow: 'hidden',
    }}>
      {showTitle && (
        <div style={{
          padding: '0.875rem 1.5rem',
          borderBottom: '2px solid #1e3a5f',
          flexShrink: 0,
          backgroundColor: '#f8fafc',
        }}>
          <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e3a5f' }}>{title}</span>
        </div>
      )}

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '4px 1fr auto auto',
        gap: '0',
        padding: '0.5rem 1.25rem 0.5rem 1.25rem',
        borderBottom: '1px solid #e2e8f0',
        backgroundColor: '#fafbfc',
        flexShrink: 0,
      }}>
        <span />
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em' }}>תפילה</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textAlign: 'center', minWidth: '70px' }}>שעה</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textAlign: 'left', minWidth: '90px', paddingRight: '12px' }}>הערה</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sorted.map((ct, idx) => {
          const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
          const accent = PRAYER_ACCENT[ct.prayer] || PRAYER_ACCENT.other;
          const isRecurring = ct.type === 'recurring';
          const rule = !isRecurring ? formatTimeRule(ct) : null;
          const isEven = idx % 2 === 0;
          const isNext = highlightNextMinyan && idx === nextIdx;

          const timeDisplay = isRecurring
            ? (ct.resolvedSlots?.length ? `${ct.resolvedSlots[0]} – ${ct.resolvedSlots[ct.resolvedSlots.length - 1]}` : '--:--')
            : (ct.resolvedTime || '--:--');

          const noteDisplay = isRecurring
            ? `כל ${ct.interval} דק׳`
            : showRuleMode && ct.type === 'relative'
              ? rule
              : (rule || '');

          return (
            <React.Fragment key={idx}>
              {/* Main row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '4px 1fr auto auto',
                gap: '0',
                alignItems: 'center',
                padding: '0.75rem 1.25rem',
                backgroundColor: isNext ? '#eff6ff' : (isEven ? '#ffffff' : '#fafbfc'),
                borderBottom: '1px solid #f1f5f9',
                borderRight: isNext ? '4px solid #1e3a5f' : 'none',
                boxShadow: isNext ? 'inset 0 0 0 1px rgba(30,58,95,0.15)' : 'none',
              }}>
                {/* Color bar */}
                <div style={{
                  width: '4px', alignSelf: 'stretch',
                  backgroundColor: accent, borderRadius: '2px',
                }} />

                {/* Prayer name */}
                <span style={{
                  fontSize: '1.05rem', fontWeight: 700, color: '#1e293b',
                  paddingRight: '12px',
                }}>
                  {label}
                </span>

                {/* Time */}
                {showRuleMode && ct.type === 'relative' && !isRecurring ? (
                  <span style={{
                    fontSize: '0.875rem', fontWeight: 600, color: '#475569',
                    textAlign: 'center', minWidth: '70px',
                  }}>
                    {rule}
                  </span>
                ) : (
                  <span style={{
                    fontSize: isRecurring ? '0.95rem' : '1.3rem',
                    fontWeight: 800, color: '#0f172a',
                    fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                    textAlign: 'center', minWidth: '70px',
                    letterSpacing: '-0.02em',
                  }}>
                    {timeDisplay}
                  </span>
                )}

                {/* Note */}
                <span style={{
                  fontSize: '0.7rem', fontWeight: 500,
                  color: isRecurring ? '#166534' : '#94a3b8',
                  textAlign: 'left', minWidth: '90px', paddingRight: '12px',
                  backgroundColor: isRecurring ? '#f0fdf4' : 'transparent',
                  border: isRecurring ? '1px solid #bbf7d0' : 'none',
                  borderRadius: isRecurring ? '99px' : '0',
                  padding: isRecurring ? '2px 8px' : '0',
                  display: 'inline-block', width: 'fit-content',
                }}>
                  {[(!showRuleMode || isRecurring) ? noteDisplay : '', ct.daysLabel].filter(Boolean).join(' · ')}
                </span>
              </div>

              {/* Full recurring slots */}
              {isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0 && (
                <div style={{
                  padding: '4px 1.25rem 8px 1.25rem',
                  paddingRight: 'calc(1.25rem + 16px)',
                  backgroundColor: isEven ? '#ffffff' : '#fafbfc',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', direction: 'ltr' }}>
                    {ct.resolvedSlots.map((slot, i) => (
                      <span key={i} style={{
                        fontSize: '0.7rem', fontWeight: 600, color: '#475569',
                        fontVariantNumeric: 'tabular-nums',
                        backgroundColor: '#f1f5f9', borderRadius: '4px', padding: '1px 5px',
                      }}>
                        {slot}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Day overrides — sub-rows */}
              {(ct.resolvedOverrides || ct.dayOverrides || []).map((ov, oi) => {
                const active = isOverrideActive(ov.days);
                const dayLabel = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
                const time = ov.resolvedTime || ov.time || '--:--';
                return (
                  <div key={`ov-${idx}-${oi}`} style={{
                    display: 'grid',
                    gridTemplateColumns: '4px 1fr auto auto',
                    gap: '0',
                    alignItems: 'center',
                    padding: '0.35rem 1.25rem',
                    paddingRight: 'calc(1.25rem + 16px)',
                    backgroundColor: active ? '#f0fdf4' : (isEven ? '#ffffff' : '#fafbfc'),
                    borderBottom: '1px solid #f1f5f9',
                    borderRight: active ? '3px solid #16a34a' : '3px solid transparent',
                  }}>
                    <span />
                    <span style={{
                      fontSize: '0.75rem', fontWeight: active ? 600 : 400,
                      color: active ? '#15803d' : '#94a3b8',
                      display: 'flex', alignItems: 'center', gap: '5px',
                    }}>
                      {active && (
                        <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16a34a', display: 'inline-block' }} />
                      )}
                      {dayLabel}
                    </span>
                    <span style={{
                      fontSize: '0.95rem', fontWeight: 700,
                      color: active ? '#15803d' : '#94a3b8',
                      fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                      textAlign: 'center', minWidth: '70px',
                    }}>
                      {time}
                    </span>
                    <span />
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Table;
